// ───────────────────────────────────────────────────────────────────────────
// DatePicker — mirrors the `@mantine/dates` `DatePicker` API, built on
// `@knitui/components` (`Box`) + the calendar family (`Calendar` + `useDatesState`)
// + dayjs. It is a thin SELECTION wrapper: it owns no cells of its own. It binds
// the shared `useDatesState` selection machine (`default` / `range` / `multiple`,
// with the live range hover-preview) to a full day-level `Calendar` and lets the
// level still zoom month↔year↔decade.
//
// Cross-platform: web + native from this single source — every prop forwarded
// here is itself cross-platform (`onMouseLeave` is a Tamagui no-op on native,
// preset presses go through `UnstyledButton`). No web-only API is reached.
//
// On the kit checklist (see `_reference/README.md`): like `Calendar`, DatePicker
// DELEGATES the leaf styling rules. Shared `size` context (2), derived
// `cell-metrics` sizing (3), theme-ramp colors (4), styled parts + marker slots
// (5/6/14) and the per-slot `styles` sugar (7) all live DOWNSTREAM in the level
// groups / cells — DatePicker just forwards the public `size` straight through,
// so re-declaring them here would reach no consumer (the same call `Calendar`
// documents). What DatePicker DOES carry: this provenance header (1),
// controlled/uncontrolled selection via `useDatesState` (8), locale + min/max +
// a11y passthrough into `Calendar` (9/10/11), per-item `getDayProps` /
// `getMonthControlProps` / `getYearControlProps` passthrough with "explicit beats
// sugar" precedence (7), and compiler-safe show/hide via the conditional preset
// subtree — never a dynamic `opacity`/`display` style prop (15). The `<Type>`
// generic is preserved through `forwardRef` (which would otherwise pin it at
// `"default"`), so the ref is forwarded via a typed `forwardRef` rather than
// `.styleable` (13) — the documented sibling-picker pattern.
// ───────────────────────────────────────────────────────────────────────────
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { Calendar } from '@unistocks/components/calendar/calendar/calendar';
import { CalendarLevel, DateStringValue } from '@unistocks/components/calendar/types';
import { isSameMonth } from '@unistocks/components/calendar/utils/is-same-month/is-same-month';
import { DatePickerPopover } from '@unistocks/components/date-picker/date-picker/date-picker-popover';
import { DatePickerProps } from '@unistocks/components/date-picker/date-picker/types';
import { useDatesState } from '@unistocks/components/date-picker/hooks/use-dates-state/use-dates-state';
import { DatePickerType } from '@unistocks/components/date-picker/types';
import { t } from '@unistocks/shared/localization/index.ts';
import { createDate, isArray, today } from '@unistocks/shared/utils/util';
import { useRef, useState } from 'react';
import { PresetList } from '../picker-presets';

/**
 * `DatePicker` — the inline day-selection picker. A full day-level `Calendar`
 * driven by the shared `useDatesState` selection machine, so it selects DATES in
 * `default` / `range` / `multiple` modes (with a live range hover-preview) while
 * the level still zooms month↔year↔decade. Values are `yyyy-MM-dd` strings;
 * selection is controlled (`value` + `onChange`) or uncontrolled (`defaultValue`).
 * Optional `presets` render quick-jump buttons beside the calendar. Forwards its
 * ref to the root and preserves the `<Type>` generic. Accent comes from the
 * active Tamagui theme. See {@link DatePickerInner} for the implementation notes.
 *
 * @example
 * <DatePicker value={value} onChange={setValue} defaultDate="2026-06-15" />
 */
export function DatePicker<Type extends DatePickerType = 'default'>(props: DatePickerProps<Type>) {
  const {
    type = 'default',
    defaultValue,
    value,
    onChange,
    allowSingleDateInRange,
    allowDeselect,
    onMouseLeave,
    __onDayClick,
    __onDayMouseEnter,
    __onPresetSelect,
    __stopPropagation,
    presets,
    size,
    numberOfColumns = 1,
    defaultLevel = 'month',
    defaultDate: _defaultDate,
    closeOnChange = true,
    withArrow = true,
    children,

    /* Popover props */
    ref,
    placement = 'bottom',
    offset,
    stayInFrame = true,
    allowFlip = true,
    resize,
    hoverable,
    disableFocus,
    disableDismissable,
    strategy,
    keepChildrenMounted,

    /* Native only props */
    androidMode,
    iOSMode,
    iOSDisplay,

    /* Calendar props */
    maxLevel,
    minLevel,
    level,
    onLevelChange,
    minDate,
    maxDate,
    nextIcon,
    previousIcon,
    date,
    onDateChange: __onDateChange,
    columnsToScroll,
    ariaLabels,
    nextLabel,
    previousLabel,
    onYearSelect,
    onMonthSelect,
    onYearMouseEnter,
    onMonthMouseEnter,
    onNextMonth,
    onPreviousMonth,
    onNextYear,
    onPreviousYear,
    onNextDecade,
    onPreviousDecade,
    fullWidth,
    withWeekNumbers,
    withCellSpacing,
    highlightToday,
    headerControlsOrder,
    __updateDateOnYearSelect,
    __updateDateOnMonthSelect,
    __preventFocus,
    __setDateRef,
    __setLevelRef,

    // MonthLevelGroup props
    weekdayFormat,
    getDayProps,
    excludeDate,
    renderDay,
    hideOutsideDates,
    hideWeekdays,
    getDayAriaLabel,
    monthLabelFormat,

    // YearLevelGroup props
    monthsListFormat,
    getMonthControlProps,
    yearLabelFormat,

    // DecadeLevelGroup props
    yearsListFormat,
    getYearControlProps,
    decadeLabelFormat,

    // Dates provider value
    locale,
    firstDayOfWeek,
    weekendDays,
    consistentWeeks = true,
    enableKeyboardNavigation,

    ...others
  } = props;

  const [open, onOpenChange] = useState(false);
  const setDateRef = useRef<((date: DateStringValue) => void) | null>(null);
  const setLevelRef = useRef<((level: CalendarLevel) => void) | null>(null);
  const useBuffer = type === 'range' || type === 'multiple';

  const {
    onDateChange,
    onRootMouseLeave,
    onHoveredDateChange,
    getControlProps,
    _value,
    setValue,
    commit,
    reset
  } = useDatesState({
    type: type as Type,
    level: 'day',
    allowDeselect,
    allowSingleDateInRange,
    value,
    defaultValue,
    onChange,
    onMouseLeave,
    deferCommit: useBuffer
  });

  // The single-date view of the current selection, used to highlight the matching
  // month/year on the zoomed-out levels.
  let defaultDate: DateStringValue | Date | undefined = _defaultDate;
  if (!defaultDate)
    defaultDate = isArray(_value)
      ? (_value.findLast((date): date is DateStringValue => date != null) ?? undefined)
      : typeof _value === 'string'
        ? _value
        : today().toISODate();

  const handleSubmit = () => {
    commit();
    onOpenChange(false);
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  // Popover props — forwarded as a group to `DatePickerBase`.
  const popoverProps = {
    ref,
    placement,
    offset,
    stayInFrame,
    allowFlip,
    resize,
    hoverable,
    disableFocus,
    disableDismissable,
    strategy,
    keepChildrenMounted
  };

  // Calendar props — forwarded as a group to `Calendar`. The composed/derived
  // props (selection wiring, `defaultDate`, `minLevel`, `hideOutsideDates`) stay
  // inline on `<Calendar>` so they always win over the grouped passthrough.
  const calendarProps = {
    maxLevel,
    level,
    onLevelChange,
    nextIcon,
    previousIcon,
    date,
    onDateChange: __onDateChange,
    columnsToScroll,
    ariaLabels,
    nextLabel,
    previousLabel,
    onYearSelect,
    onMonthSelect,
    onYearMouseEnter,
    onMonthMouseEnter,
    onNextMonth,
    onPreviousMonth,
    onNextYear,
    onPreviousYear,
    onNextDecade,
    onPreviousDecade,
    fullWidth,
    withWeekNumbers,
    withCellSpacing,
    highlightToday,
    headerControlsOrder,
    __updateDateOnYearSelect,
    __updateDateOnMonthSelect,
    __preventFocus,
    __stopPropagation,
    weekdayFormat,
    excludeDate,
    renderDay,
    hideWeekdays,
    getDayAriaLabel,
    monthLabelFormat,
    monthsListFormat,
    yearLabelFormat,
    yearsListFormat,
    decadeLabelFormat,
    locale,
    firstDayOfWeek,
    weekendDays,
    consistentWeeks,
    enableKeyboardNavigation,
    minDate,
    maxDate,
    __setDateRef: setDateRef,
    __setLevelRef: setLevelRef
  };

  return (
    <DatePickerPopover open={open} onOpenChange={onOpenChange} {...popoverProps}>
      <DatePickerPopover.Trigger asChild>{children}</DatePickerPopover.Trigger>

      <DatePickerPopover.Content {...others}>
        {withArrow ? <DatePickerPopover.Content.Arrow /> : null}

        <Calendar
          {...calendarProps}
          size={size}
          onMouseLeave={onRootMouseLeave}
          numberOfColumns={numberOfColumns}
          defaultLevel={defaultLevel}
          defaultDate={defaultDate}
          minLevel={minLevel ?? 'month'}
          hideOutsideDates={hideOutsideDates ?? numberOfColumns !== 1}
          __onDayMouseEnter={(event, date) => {
            onHoveredDateChange(date);
            __onDayMouseEnter?.(event, date);
          }}
          __onDayClick={(event, date) => {
            if (type === 'default' && closeOnChange) onOpenChange(false);
            onDateChange(date);
            __onDayClick?.(event, date);
          }}
          getDayProps={date => ({
            ...getControlProps(date),
            ...getDayProps?.(date)
          })}
          getMonthControlProps={date => ({
            selected: defaultDate ? isSameMonth(date, defaultDate) : false,
            ...getMonthControlProps?.(date)
          })}
          getYearControlProps={date => ({
            selected: defaultDate ? createDate(date).year === createDate(defaultDate).year : false,
            ...getYearControlProps?.(date)
          })}
        />

        {presets ? (
          <PresetList
            presets={presets}
            setDateRef={setDateRef}
            setLevelRef={setLevelRef}
            level="month"
            setValue={setValue}
            __onPresetSelect={__onPresetSelect}
          />
        ) : null}

        {useBuffer ? (
          <SubmitButtons
            w={'100%'}
            mt={'$3'}
            cancelButtonText={t('::Cancel')}
            submitButtonText={t('::Select')}
            submitButtonType="button"
            onCancel={handleCancel}
            onSubmit={handleSubmit}
          />
        ) : null}
      </DatePickerPopover.Content>
    </DatePickerPopover>
  );
}

DatePicker.displayName = 'DatePicker';
